import React, { useEffect } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { AnyAction } from "redux";

import { Components, Services, Logger, Models } from "@sendum/core";

import { VisibilityRootState } from "./store/VisibilityStore";
import * as VisibilityActions from "./store/VisibilityActions";
import { VisibilityGraphsTimeline } from "./components/VisibilityGraphsTimeline";
import { VisibilityMarkenSensors } from "./components/VisibilityMarkenSensors";
import { VisibilityHistoryTable } from "./components/VisibilityHistoryTable";
import { GoNoGo } from "./components/GoNoGo";
import { Landing } from "./components/Landing";
import { Dewar } from "./components/Dewar/Dewar";

import cn from "./VisibilityMarken.module.scss";

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const logger = new Logger("VisibilityMarkenPublic");
const MAX_ACTIVE_SENSORS = 3;

export interface IProps {
	orderId?: string;
	deviceId?: string;
	isLiveLegExtended?: boolean;
}

export const VisibilityMarkenPublic = ({ orderId, isLiveLegExtended }: IProps): JSX.Element => {
	const dispatch = useDispatch();
	const shipment = useSelector<VisibilityRootState, Models.Shipment | undefined>((state: VisibilityRootState) => state.Visibility.shipment, shallowEqual);
	const error = useSelector<VisibilityRootState, Error | undefined>((state: VisibilityRootState) => state.Visibility.error, shallowEqual);
	const isLoadingShipment = useSelector<VisibilityRootState, boolean | undefined>((state: VisibilityRootState) => state.Visibility.isLoadingShipment, shallowEqual);
	const device = useSelector<VisibilityRootState, Models.Device | undefined>((state: VisibilityRootState) => state.Visibility.device, shallowEqual);

	useEffect(() => {
		Services.MQTT.connect((message: string, topic: string): void => {
			logger.info(topic);
			dispatch(VisibilityActions.updateStateByMqtt(message, topic) as unknown as AnyAction);
		});

		return () => {
			dispatch(VisibilityActions.resetState() as unknown as AnyAction);
			Services.MQTT.disconnect();
		};
	}, [dispatch]);

	useEffect(() => {
		dispatch(VisibilityActions.resetState() as unknown as AnyAction);
		if (orderId) {
			dispatch(VisibilityActions.getShipmentPublic(orderId, undefined, isLiveLegExtended) as unknown as AnyAction);
		}
	}, [dispatch, orderId, isLiveLegExtended]);

	if (!orderId && !shipment) {
		return (<Landing className={cn.landing} redirectPath='/visibility/public'/>);
	}

	return (
		<div className={cn.visibility}>
			<Components.LoadingComponent loading={isLoadingShipment} className={cn.loading} />
			{!!error && (<Components.ErrorComponent error={error} />)}
			{shipment && !isLoadingShipment && !error && (
				<div className={cn.visibility__column}>
					<Dewar/>
					<div className={cn.visibility__sensors}>
						<VisibilityMarkenSensors maxActiveSensors={MAX_ACTIVE_SENSORS}>
							<GoNoGo />
						</VisibilityMarkenSensors>
					</div>
					<div className={`${cn.visibility__map_and_graphs} ${cn.map_and_graphs}`}>
						<div className={cn.map_and_graphs__row}>
							{!!device && (
								<div className={cn.map_and_graphs__graph}>
									<VisibilityGraphsTimeline maxSensors={MAX_ACTIVE_SENSORS} />
								</div>
							)}
						</div>
					</div>
					<div className={cn.visibility__history}>
						<VisibilityHistoryTable />
					</div>
				</div>
			)}
		</div>
	);
};
